/**
 * @component ry-toggle-button
 * @category  form
 * @summary   Single-selection toggle button — buttons sharing a `name` attribute form an exclusive group where exactly one is pressed at a time (radio-group behavior on a button surface). Click presses; pressing one auto-unpresses its name-siblings; clicking the active button does NOT unpress it (one-of-N selection, not toggle).
 *
 * Reference: Fomantic UI does not have a direct equivalent; the closest is
 * a `<ry-button-group>` of pressed buttons coordinated manually. ry-toggle-
 * button bakes that coordination into the component via a global registry
 * keyed by `[name]`.
 *
 * @attribute name      string?              Group key. Toggle buttons sharing the same `name` form an exclusive group. Two unrelated groups use different names.
 * @attribute value     string?              Submitted/identifying value for this button. Emitted in `ry:change.detail.value` when the button becomes pressed.
 * @attribute pressed   boolean   default=false   Initial pressed state. Setting `pressed` on multiple same-name buttons at mount: the LAST one to register wins (others are auto-unpressed in registration order).
 * @attribute disabled  boolean   default=false   Non-interactive. Click + keyboard activation suppressed; `aria-disabled="true"`, `tabindex="-1"`.
 * @attribute icon      string?              Icon name from the registry. Pure attribute — the consumer's theme/CSS handles rendering the icon glyph.
 *
 * @property  pressed   boolean   Live pressed state. Setting to `true` unpresses name-siblings + fires `ry:change`. Setting to `false` clears pressed without affecting siblings.
 * @property  disabled  boolean   Live disabled state.
 * @property  name      string    Live group key. Changing this re-registers in the new group + checks the new group for conflicts.
 * @property  value     string    Live submit value.
 *
 * @event ry:beforechange
 *        detail: { value: string, label: 'pressed' | 'unpressed', pressed: boolean, previous: boolean }
 *        fires:  at the top of the `pressed` setter — click, Enter/Space, `el.pressed = x`, and the group's auto-unpress of a sibling — before the attribute changes, and only when `pressed` would actually transition. `pressed` is the NEXT state, `previous` the current one.
 *        cancelable: preventDefault() leaves `pressed` as it was; `ry:change` does not fire and (for a press) siblings are not unpressed.
 *
 * @event ry:change
 *        detail: { value: string, label: 'pressed' | 'unpressed' }
 *        fires:  every time `pressed` transitions. Does NOT fire when setting `pressed = true` on an already-pressed button (no transition).
 *
 * @keyboard  Enter / Space — activate (when not disabled). Tab moves focus normally; the host has `tabindex="0"` by default (`-1` when disabled).
 *
 * @state     `[pressed]` attribute is the source of truth; `aria-pressed="true|false"` mirrors it.
 *
 * @content   Free-form — usually just the label text or an icon. The wrapper does NOT replace innerHTML.
 *
 * @dom
 *   ry-toggle-button[name?][value?][pressed?][disabled?]   role="button"  aria-pressed="true|false"  tabindex="0|-1"
 *     > {authored children}
 *
 * @gotcha  Group coordination is ONE-WAY. Clicking the active button does NOT unpress it (this is a radio-group pattern). To support unpress-on-click, set `el.pressed = false` programmatically in your click handler. For free toggle behavior (independent pressed states), use `<ry-button pressed>` instead — that's the binary toggle primitive.
 *
 * @gotcha  Same-name buttons across DIFFERENT containers (`<form>`, `<section>`, `<fieldset>`, `<ry-section>`) emit a one-time console.warn — this usually indicates two unrelated groups accidentally sharing a name. Use distinct names per logical group OR explicitly accept the global coupling.
 *
 * @gotcha  `name` is a property AND an attribute. Setting `el.name = ""` clears the registry entry but does NOT clear the `name` HTML attribute (the property setter calls `removeAttribute`, but inspection shows the attribute is removed). After clearing name, the button is ungrouped and operates independently.
 *
 * @gotcha  The global registry is process-wide — toggling buttons survive across mount/unmount via `setup`/`teardown`. Buttons removed from DOM via `disconnectedCallback` ARE auto-unregistered (teardown handles it), so detached buttons don't accumulate.
 *
 * @example basic-group
 * ```html
 * <ry-toggle-button name="view" value="list" pressed>List</ry-toggle-button>
 * <ry-toggle-button name="view" value="grid">Grid</ry-toggle-button>
 * <ry-toggle-button name="view" value="card">Card</ry-toggle-button>
 * ```
 *
 * @example icon-buttons
 * ```html
 * <ry-toggle-button name="align" value="left" icon="align-left" pressed></ry-toggle-button>
 * <ry-toggle-button name="align" value="center" icon="align-center"></ry-toggle-button>
 * <ry-toggle-button name="align" value="right" icon="align-right"></ry-toggle-button>
 * ```
 *
 * @example in-button-group
 * ```html
 * <ry-button-group aria-label="View mode">
 *   <ry-toggle-button name="view" value="list" pressed>List</ry-toggle-button>
 *   <ry-toggle-button name="view" value="grid">Grid</ry-toggle-button>
 * </ry-button-group>
 * ```
 *
 * @example disabled-option
 * ```html
 * <ry-toggle-button name="plan" value="free" pressed>Free</ry-toggle-button>
 * <ry-toggle-button name="plan" value="pro">Pro</ry-toggle-button>
 * <ry-toggle-button name="plan" value="enterprise" disabled>Enterprise (locked)</ry-toggle-button>
 * ```
 *
 * @tutorial click-presses-and-emits-ry-change
 * ```js
 * const el = mount('<ry-toggle-button name="view" value="list">List</ry-toggle-button>');
 * const fired = [];
 * el.addEventListener('ry:change', (e) => fired.push(e.detail));
 * el.click();
 * await expectEqual(el.pressed, true);
 * await expectAttr(el, 'aria-pressed', 'true');
 * await expectEqual(fired.length, 1);
 * await expectEqual(fired[0].value, 'list');
 * await expectEqual(fired[0].label, 'pressed');
 * ```
 *
 * @tutorial click-active-button-does-not-unpress
 * ```js
 * // Wrong assumption: clicking a pressed button toggles it off (free toggle).
 * // ry-toggle-button is RADIO-GROUP style — click only PRESSES; one-of-N stays
 * // selected. To unpress, set el.pressed = false programmatically.
 * const el = mount('<ry-toggle-button name="view" value="x" pressed>X</ry-toggle-button>');
 * const fired = [];
 * el.addEventListener('ry:change', () => fired.push(1));
 * el.click();
 * await expectEqual(el.pressed, true, 'still pressed');
 * await expectEqual(fired.length, 0, 'no ry:change — no transition');
 * ```
 *
 * @tutorial pressing-one-unpresses-siblings
 * ```js
 * const wrap = mount(`
 *   <div>
 *     <ry-toggle-button id="a" name="grp" value="a" pressed>A</ry-toggle-button>
 *     <ry-toggle-button id="b" name="grp" value="b">B</ry-toggle-button>
 *     <ry-toggle-button id="c" name="grp" value="c">C</ry-toggle-button>
 *   </div>
 * `);
 * const a = wrap.querySelector('#a');
 * const b = wrap.querySelector('#b');
 * const c = wrap.querySelector('#c');
 * await expectEqual(a.pressed, true);
 * b.click();
 * await expectEqual(a.pressed, false, 'a auto-unpressed');
 * await expectEqual(b.pressed, true);
 * await expectEqual(c.pressed, false);
 * ```
 *
 * @tutorial different-names-do-not-coordinate
 * ```js
 * // Wrong assumption: all toggle buttons coordinate. Only buttons sharing
 * // the same [name] form a group.
 * const wrap = mount(`
 *   <div>
 *     <ry-toggle-button id="a" name="g1" value="a" pressed>A</ry-toggle-button>
 *     <ry-toggle-button id="b" name="g2" value="b" pressed>B</ry-toggle-button>
 *   </div>
 * `);
 * await expectEqual(wrap.querySelector('#a').pressed, true);
 * await expectEqual(wrap.querySelector('#b').pressed, true, 'independent groups co-exist');
 * ```
 *
 * @tutorial disabled-suppresses-click
 * ```js
 * const el = mount('<ry-toggle-button name="x" value="x" disabled>X</ry-toggle-button>');
 * await expectAttr(el, 'aria-disabled', 'true');
 * await expectAttr(el, 'tabindex', '-1');
 * const fired = [];
 * el.addEventListener('ry:change', () => fired.push(1));
 * el.click();
 * await expectEqual(el.pressed, false);
 * await expectEqual(fired.length, 0);
 * ```
 *
 * @tutorial keyboard-activates
 * ```js
 * const el = mount('<ry-toggle-button name="x" value="x">X</ry-toggle-button>');
 * el.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', bubbles: true }));
 * await expectEqual(el.pressed, true, 'Enter presses');
 * // Already pressed — Space should NOT unpress (radio-group semantics)
 * el.dispatchEvent(new KeyboardEvent('keydown', { key: ' ', bubbles: true }));
 * await expectEqual(el.pressed, true, 'Space on pressed: stays pressed');
 * ```
 *
 * @tutorial programmatic-unpress
 * ```js
 * // The radio-group click-pressed-only behavior is overridable via the property
 * // setter — setting .pressed = false directly DOES unpress.
 * const el = mount('<ry-toggle-button name="x" value="x" pressed>X</ry-toggle-button>');
 * const fired = [];
 * el.addEventListener('ry:change', (e) => fired.push(e.detail.label));
 * el.pressed = false;
 * await expectEqual(el.pressed, false);
 * await expectAttr(el, 'aria-pressed', 'false');
 * await expectEqual(fired.length, 1);
 * await expectEqual(fired[0], 'unpressed');
 * ```
 *
 * @tutorial name-change-re-registers
 * ```js
 * // Wrong assumption: name is read once at mount. Changing it via the
 * // property setter re-registers the button into the new group.
 * const wrap = mount(`
 *   <div>
 *     <ry-toggle-button id="a" name="g1" value="a" pressed>A</ry-toggle-button>
 *     <ry-toggle-button id="b" name="g2" value="b" pressed>B</ry-toggle-button>
 *   </div>
 * `);
 * const a = wrap.querySelector('#a');
 * a.name = 'g2';  // Move A into g2
 * // Pressing A should now unpress B (same group), not the old g1
 * a.click();  // already pressed → no-op
 * a.pressed = false;
 * a.pressed = true;
 * await expectEqual(wrap.querySelector('#b').pressed, false, 'B got unpressed by A in g2');
 * ```
 *
 * @tutorial beforechange-veto
 * ```js
 * // preventDefault() on ry:beforechange keeps the button where it was —
 * // the click does not press it, the group is untouched, ry:change is silent.
 * const wrap = mount(`
 *   <div>
 *     <ry-toggle-button id="a" name="grp" value="a" pressed>A</ry-toggle-button>
 *     <ry-toggle-button id="b" name="grp" value="b">B</ry-toggle-button>
 *   </div>
 * `);
 * const a = wrap.querySelector('#a');
 * const b = wrap.querySelector('#b');
 * const changed = [];
 * b.addEventListener('ry:change', () => changed.push(1));
 * let seen = null;
 * b.addEventListener('ry:beforechange', (e) => { seen = e.detail; e.preventDefault(); }, { once: true });
 * b.click();
 * await expectEqual(seen.pressed, true, 'detail.pressed is the NEXT state');
 * await expectEqual(seen.previous, false, 'detail.previous is the current state');
 * await expectEqual(b.pressed, false, 'vetoed: B stays unpressed');
 * await expectEqual(a.pressed, true, 'vetoed: A stays pressed');
 * await expectEqual(changed.length, 0, 'ry:change did not fire');
 * b.click();
 * await expectEqual(b.pressed, true, 'without the veto, B is pressed');
 * await expectEqual(a.pressed, false, 'and A is auto-unpressed');
 * await expectEqual(changed.length, 1);
 * ```
 *
 * @see ry-button
 * @see ry-button-group
 * @see ry-tile-radio
 */

import { RyElement } from '../core/ry-element.js';
import { getIcon } from '../core/ry-icons.js';
import type { ChangeDetail } from '../types.js';

interface ToggleButtonBeforeChangeDetail extends ChangeDetail {
  pressed: boolean;
  previous: boolean;
}

// Container types that define logical grouping boundaries
const BOUNDARY_SELECTORS = ['form', 'section', 'fieldset', 'ry-section'];

// Global registry: name -> Set of toggle button instances
const registry = new Map<string, Set<RyToggleButton>>();

// Track which name groups have already warned about conflicts
const warnedGroups = new Set<string>();

/**
 * Find the nearest boundary ancestor (form, section, fieldset)
 */
function findBoundaryAncestor(el: Element): Element | null {
  let current = el.parentElement;
  while (current) {
    if (BOUNDARY_SELECTORS.some(sel => current!.matches(sel))) {
      return current;
    }
    current = current.parentElement;
  }
  return null;
}

/**
 * Check if buttons with the same name are in different boundary containers
 */
function checkForConflicts(name: string): void {
  if (warnedGroups.has(name)) return;

  const buttons = registry.get(name);
  if (!buttons || buttons.size < 2) return;

  const boundaries = new Map<Element | null, RyToggleButton[]>();

  for (const btn of buttons) {
    const boundary = findBoundaryAncestor(btn);
    const group = boundaries.get(boundary) ?? [];
    group.push(btn);
    boundaries.set(boundary, group);
  }

  // If buttons are in more than one boundary container, warn
  if (boundaries.size > 1) {
    const containerDescriptions: string[] = [];
    for (const [boundary] of boundaries) {
      if (boundary) {
        const id = boundary.id ? `#${boundary.id}` : '';
        const tag = boundary.tagName.toLowerCase();
        containerDescriptions.push(`${tag}${id}`);
      } else {
        containerDescriptions.push('(no container)');
      }
    }

    console.warn(
      `ry-toggle-button: Buttons with name="${name}" span multiple containers (${containerDescriptions.join(', ')}). Intentional?`
    );
    warnedGroups.add(name);
  }
}

export class RyToggleButton extends RyElement {
  static observedAttributes = ['pressed', 'disabled', 'name', 'value'] as const;

  #registered = false;

  setup(): void {
    // Inject the registered SVG glyph for [icon] when authored empty.
    // Mirrors ry-button's icon-only mode so consumers don't have to author <ry-icon> children.
    if (this.icon && !this.innerHTML.trim()) {
      this.innerHTML = getIcon(this.icon);
    }

    // Set up accessibility
    this.setAttribute('role', 'button');
    this.setAttribute('aria-pressed', String(this.pressed));

    // Initial-disabled propagation: mirror the disabled setter for the
    // attribute case so [disabled] at mount applies aria-disabled + tabindex=-1
    if (this.disabled) {
      this.setAttribute('aria-disabled', 'true');
      this.setAttribute('tabindex', '-1');
    } else if (!this.hasAttribute('tabindex')) {
      this.setAttribute('tabindex', '0');
    }

    // Register in the global registry
    this.#register();

    // Event handlers
    this.on(this, 'click', this.#handleClick);
    this.on(this, 'keydown', this.#handleKeydown);
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) return;
    if (!this.hasAttribute('data-ry-initialized')) return;
    if (name === 'disabled') {
      if (newValue !== null) {
        this.setAttribute('aria-disabled', 'true');
        this.setAttribute('tabindex', '-1');
      } else {
        this.removeAttribute('aria-disabled');
        this.setAttribute('tabindex', '0');
      }
    } else if (name === 'pressed') {
      this.setAttribute('aria-pressed', newValue !== null ? 'true' : 'false');
    }
  }

  teardown(): void {
    this.#unregister();
  }

  #register(): void {
    const name = this.name;
    if (!name) return;

    if (!registry.has(name)) {
      registry.set(name, new Set());
    }
    registry.get(name)!.add(this);
    this.#registered = true;

    // Check for conflicts after a microtask to allow all buttons to register
    queueMicrotask(() => checkForConflicts(name));
  }

  #unregister(): void {
    if (!this.#registered) return;

    const name = this.name;
    if (name && registry.has(name)) {
      registry.get(name)!.delete(this);
      if (registry.get(name)!.size === 0) {
        registry.delete(name);
        warnedGroups.delete(name);
      }
    }
    this.#registered = false;
  }

  #handleClick = (event: MouseEvent): void => {
    if (this.disabled) {
      event.preventDefault();
      return;
    }

    // Toggle this button on (don't toggle off if already pressed)
    if (!this.pressed) {
      this.pressed = true;
    }
  };

  #handleKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.click();
    }
  };

  #unpressOthers(): void {
    const name = this.name;
    if (!name) return;

    const siblings = registry.get(name);
    if (!siblings) return;

    for (const btn of siblings) {
      if (btn !== this && btn.pressed) {
        btn.pressed = false;
      }
    }
  }

  // --- Public API ---

  get pressed(): boolean {
    return this.hasAttribute('pressed');
  }

  set pressed(value: boolean) {
    const wasPressed = this.pressed;

    // Interception point — every path (click, keyboard, programmatic, sibling
    // auto-unpress) lands here. Only a real transition is vetoable.
    if (wasPressed !== value && !this.emit<ToggleButtonBeforeChangeDetail>('beforechange', {
      value: this.value,
      label: value ? 'pressed' : 'unpressed',
      pressed: value,
      previous: wasPressed,
    })) return;

    if (value) {
      this.setAttribute('pressed', '');
      this.setAttribute('aria-pressed', 'true');
      this.#unpressOthers();
    } else {
      this.removeAttribute('pressed');
      this.setAttribute('aria-pressed', 'false');
    }

    // Only emit if state actually changed
    if (wasPressed !== value) {
      this.emit<ChangeDetail>('change', {
        value: this.value,
        label: value ? 'pressed' : 'unpressed'
      });
    }
  }

  get disabled(): boolean {
    return this.hasAttribute('disabled');
  }

  set disabled(value: boolean) {
    if (value) {
      this.setAttribute('disabled', '');
      this.setAttribute('aria-disabled', 'true');
      this.setAttribute('tabindex', '-1');
    } else {
      this.removeAttribute('disabled');
      this.removeAttribute('aria-disabled');
      this.setAttribute('tabindex', '0');
    }
  }

  get name(): string {
    return this.getAttribute('name') ?? '';
  }

  set name(value: string) {
    const oldName = this.name;
    if (oldName === value) return;

    // Re-register with new name
    this.#unregister();
    if (value) {
      this.setAttribute('name', value);
    } else {
      this.removeAttribute('name');
    }
    this.#register();
  }

  get value(): string {
    return this.getAttribute('value') ?? '';
  }

  set value(val: string) {
    this.setAttribute('value', val);
  }

  get icon(): string {
    return this.getAttribute('icon') ?? '';
  }

  set icon(val: string) {
    this.setAttribute('icon', val);
  }
}

customElements.define('ry-toggle-button', RyToggleButton);
